import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useEntityList, createEntity, updateEntity } from '../data/entities';

// The copilot turn spine, shared by the CopilotPanel (text) and the
// AgentDock (voice): one aiSession per user, turns are queued aiMessage
// docs the agentOS worker drives, approval is the one client-writable
// field. Both surfaces read and write the SAME docs, so a request spoken
// to NoVA shows up as a plan card in the panel and the panel's plan is
// what the voice reads back.
//
// Every turn carries `page` (route) and `surface` ('dev' inside the Dev
// Console, else 'instance') so triage/planning know where the user is.
//
// `enabled` gates the Firestore listeners: the panel enables them when it
// opens, the dock only once a turn has been delegated.

// Cross-surface nudge: the dock asks the panel to open when it delegates.
export const COPILOT_OPEN_EVENT = 'nova:copilot-open';
export const requestCopilotOpen = () => { try { window.dispatchEvent(new CustomEvent(COPILOT_OPEN_EVENT)); } catch { /* SSR / tests */ } };

export default function useCopilotTurn({ enabled = false } = {}) {
  const { user: me } = useAuth();
  const location = useLocation();
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState('');
  const creatingRef = useRef(false);

  const { docs: sessions } = useEntityList('aiSession', {}, { orderBy: 'createdAt', dir: 'desc', enabled: enabled && !!me });
  const scope = useMemo(() => ({ aiSession: sessionId }), [sessionId]);
  const { docs: messages } = useEntityList('aiMessage', scope, { orderBy: 'createdAt', dir: 'asc', enabled: enabled && !!sessionId });

  // Latest session, or lazily create one on first use.
  useEffect(() => {
    if (!enabled || sessionId || sessions === null || !me) return;
    if (sessions.length) {
      setSessionId(sessions[0].id);
      return;
    }
    if (creatingRef.current) return;
    creatingRef.current = true;
    createEntity('aiSession', {}, { title: 'New session', createdBy: me.uid })
      .then(setSessionId)
      .catch((e) => setError(e.message))
      .finally(() => { creatingRef.current = false; });
  }, [enabled, sessionId, sessions, me]);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    if (!me) throw new Error('sign in to use the copilot');
    const id = await createEntity('aiSession', {}, { title: 'New session', createdBy: me.uid });
    setSessionId(id);
    return id;
  }, [sessionId, me]);

  const newSession = useCallback(async () => {
    const id = await createEntity('aiSession', {}, { title: 'New session', createdBy: me?.uid });
    setSessionId(id);
    return id;
  }, [me]);

  const context = useCallback(() => ({
    page: `${location.pathname}${location.search}`.slice(0, 300),
    surface: location.pathname.startsWith('/dev') ? 'dev' : 'instance',
  }), [location.pathname, location.search]);

  const busy = (messages || []).some((m) => ['queued', 'planning', 'executing'].includes(m.status));

  // Queue a user turn. Returns the message id.
  const enqueue = useCallback(async (content) => {
    const text = String(content || '').trim().slice(0, 8000);
    if (!text) throw new Error('empty message');
    const sid = await ensureSession();
    setError('');
    return createEntity('aiMessage', { aiSession: sid }, { role: 'user', content: text, uid: me.uid, status: 'queued', ...context() });
  }, [ensureSession, me, context]);

  // Approve/reject a plan — by id, or the newest plan waiting on THIS user.
  const approve = useCallback(async (decision, msgId = null) => {
    const sid = sessionId;
    if (!sid) throw new Error('no copilot session');
    let id = msgId;
    if (!id) {
      const waiting = [...(messages || [])].reverse().find((m) => m.role === 'user' && m.status === 'awaiting_approval' && m.uid === me?.uid);
      if (!waiting) throw new Error('no plan is waiting for your approval');
      id = waiting.id;
    }
    return updateEntity('aiMessage', { aiSession: sid }, id, { approval: decision === 'rejected' ? 'rejected' : 'approved' });
  }, [sessionId, messages, me]);

  return { me, sessionId, scope, sessions, messages, busy, error, setError, enqueue, approve, newSession, ensureSession, context };
}
